/* Copyright (c) 2020-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useRef, useState, ChangeEvent } from "react";
import { Status } from "@nordcloud/gnui/dist/components/input/types";
import { If, Unless } from "react-if";
import styled from "styled-components";
import {
  Input,
  Checkbox,
  Heading,
  Button,
  FlexContainer,
  theme,
  Spacer,
  Tooltip,
} from "@nordcloud/gnui";
import { RolesSortFields, SortOrder, Role } from "~/generated/graphql";
import { Sidebar } from "~/components";
import { LoaderWrap } from "~/components/Loader";
import { useDisclosure } from "~/hooks";
import { inflect, isNonEmpty, isNotNil } from "~/tools";
import { useGetRoles } from "~/views/permissions/Roles/hooks/useGetRoles/useGetRoles";
import { isRoleEditable } from "~/views/permissions/utils";

type Props = {
  ids: string[];
  onSelect: (data: string[]) => void;
  isLoading: boolean;
  isError?: Status;
};

export function SelectRolesSidebar({
  onSelect,
  ids,
  isLoading,
  isError,
}: Props) {
  const [filteredOptions, setFilteredOptions] = useState<Role[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const { isOpen, open, close } = useDisclosure();
  const { data, loading } = useGetRoles({
    page: "1",
    limit: "100",
    sort: {
      field: RolesSortFields.Name,
      order: SortOrder.Asc,
    },
  });

  const roles = data.filter(isNotNil);

  useEffect(() => {
    setFilteredOptions(roles);
  }, [isOpen]);

  const filterBySearch = (event: ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.toLowerCase();
    setFilteredOptions(
      roles.filter(
        ({ name, id }) =>
          name.toLowerCase().includes(searchValue) ||
          id.toLowerCase().includes(searchValue)
      )
    );
  };

  const selectRoles = (role: Role) => {
    const roleId = role.id;
    if (ids.includes(roleId)) {
      const newValues = ids.filter((element) => element !== roleId);
      onSelect(newValues);
    } else {
      onSelect([...ids, roleId]);
    }
  };

  const inputValue = isNonEmpty(ids)
    ? `${ids.length} ${inflect("Role")(ids.length)} assigned`
    : "";

  const onClear = () => {
    onSelect([]);
    close();
  };

  return (
    <>
      <Input
        id="role"
        placeholder="Role"
        value={inputValue}
        icon="sidebar"
        disabled={isLoading}
        onClick={open}
        status={isError}
        readOnly
      />
      <Sidebar
        headerStyles={{ zIndex: 3 }}
        title="Select Role"
        isOpen={isOpen}
        onClick={close}
        footer={
          <FlexContainer
            alignItems="center"
            justifyContent="space-between"
            p={3}
            css={{ width: "100%" }}
          >
            <Button onClick={close}>
              {ids.length} {inflect("Role")(ids.length)} assigned
            </Button>
            <Button severity="low" onClick={onClear}>
              Clear
            </Button>
          </FlexContainer>
        }
      >
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search"
          onChange={filterBySearch}
        />
        <Spacer height={theme.spacing.spacing05} />
        <LoaderWrap loading={loading} inContent>
          {filteredOptions?.map((role: Role) => (
            <FlexContainer
              key={role?.id}
              justifyContent="flex-start"
              alignItems="center"
              onClick={() => isRoleEditable(role) && selectRoles(role)}
              pb={3}
              pt={3}
              css={{
                cursor: !isRoleEditable(role) ? "no-drop" : "pointer",
                borderBottom: theme.borders.grey,
              }}
            >
              <SwitchWrap>
                <Checkbox
                  withoutLabel
                  checked={ids.includes(role.id)}
                  disabled={!isRoleEditable(role)}
                  readOnly
                />
              </SwitchWrap>
              <FlexContainer direction="column" alignItems="flex-start">
                <Unless condition={isRoleEditable(role)}>
                  <Tooltip
                    showTimeout={0}
                    caption="It is a system role. Not allowed to edit"
                  >
                    <ItemHeading level={4} disabled>
                      {role.name}
                    </ItemHeading>
                  </Tooltip>
                </Unless>
                <If condition={isRoleEditable(role)}>
                  <ItemHeading level={4}>{role.name}</ItemHeading>
                </If>
              </FlexContainer>
            </FlexContainer>
          ))}
        </LoaderWrap>
      </Sidebar>
    </>
  );
}

export const ItemHeading = styled(Heading)<{ disabled?: boolean }>`
  word-break: break-all;
  text-align: left;
  margin-right: ${theme.spacing.spacing01};
  color: ${({ disabled }) => disabled && theme.color.text.text03};
`;

export const SwitchWrap = styled.div`
  pointer-events: none;
  padding: 0 ${theme.spacing.spacing04} 0 ${theme.spacing.spacing01};
`;
